import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Edit, 
  Trash2, 
  MapPin,
  RefreshCw
} from 'lucide-react';
import { apiService } from '../../services/apiService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import DoubleScrollbar from '../../components/UI/DoubleScrollbar';
import toast from 'react-hot-toast';
import Modal from '../../components/UI/Modal';
import Input from '../../components/UI/Input';
import { formatDateTime } from '../../utils/formatDateTime';
import { useSettings } from '../../contexts/SettingsContext';
import Button from '../../components/UI/Button';

interface Location {
  _id: string;
  name: string;
  address?: string | { street: string };
  description?: string;
  status: 'active' | 'inactive';
  createdAt: string;
  updatedAt: string;
}

const LocationsPage: React.FC = () => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [newLocation, setNewLocation] = useState({
    name: '',
    address: '',
    description: '',
    status: 'active' as 'active' | 'inactive',
  });

  const { settings } = useSettings();

  useEffect(() => {
    fetchLocations();
  }, []);

  // Soft refresh for locations
  useEffect(() => {
    const onRefresh = () => {
      apiService.invalidateMasterDataCache();
      fetchLocations();
    };
    window.addEventListener('app:refresh', onRefresh as any);
    return () => window.removeEventListener('app:refresh', onRefresh as any);
  }, []);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/locations?limit=1000'); // Request all locations
      setLocations(response.data.locations || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
      toast.error('Failed to fetch locations');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLocation = async () => {
    try {
      // Map frontend fields to backend model structure
      const addData = {
        name: newLocation.name,
        description: newLocation.description,
        isActive: newLocation.status === 'active',
        // Handle address - if it's a string, use it as street, otherwise keep as is
        address: typeof newLocation.address === 'string' 
          ? { street: newLocation.address }
          : newLocation.address
      };
      
      const response = await apiService.post('/locations', addData);
      toast.success('Location added successfully');
      
      // Immediately clear master data cache and force refresh
      apiService.invalidateMasterDataCache();
      
      setShowAddModal(false);
      setNewLocation({
        name: '',
        address: '',
        description: '',
        status: 'active',
      });
      
      // Force refresh to get updated data with a small delay to ensure cache is cleared
      setTimeout(async () => {
        await fetchLocations();
      }, 100);
    } catch (error) {
      console.error('Error adding location:', error);
      toast.error('Failed to add location');
    }
  };

  const handleEditLocation = async () => {
    if (!editingLocation) return;
    
    try {
      // Map frontend fields to backend model structure
      const updateData = {
        name: editingLocation.name,
        description: editingLocation.description,
        isActive: editingLocation.status === 'active',
        // Handle address - if it's a string, use it as street, otherwise keep as is
        address: typeof editingLocation.address === 'string' 
          ? { street: editingLocation.address }
          : editingLocation.address
      };
      
      const response = await apiService.put(`/locations/${editingLocation._id}`, updateData);
      toast.success('Location updated successfully');
      
      // Immediately clear master data cache and force refresh
      apiService.invalidateMasterDataCache();
      
      setShowEditModal(false);
      setEditingLocation(null);
      
      // Force refresh to get updated data with a small delay to ensure cache is cleared
      setTimeout(async () => {
        await fetchLocations();
      }, 100);
    } catch (error) {
      console.error('Error updating location:', error);
      toast.error('Failed to update location');
    }
  };

  const handleDeleteLocation = async (locationId: string) => {
    if (window.confirm('Are you sure you want to delete this location?')) {
      try {
        const response = await apiService.delete(`/locations/${locationId}`);
        toast.success('Location deleted successfully');
        
        // Immediately clear master data cache and force refresh
        apiService.invalidateMasterDataCache();
        
        // Force refresh to get updated data with a small delay to ensure cache is cleared
        setTimeout(async () => {
          await fetchLocations();
        }, 100);
      } catch (error) {
        console.error('Error deleting location:', error);
        toast.error('Failed to delete location');
      }
    }
  };

  const openEditModal = (location: Location) => {
    setEditingLocation(location);
    setShowEditModal(true);
  };

  const filteredLocations = locations.filter(location => {
    const addressString = typeof location.address === 'string'
      ? location.address
      : (typeof location.address === 'object' && location.address?.street)
        ? location.address.street
        : '';
    const matchesSearch = location.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      addressString.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (location.description?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);
    const matchesStatus = !statusFilter || location.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const exportLocations = async () => {
    try {
      await apiService.download('/products/locations/export', 'locations.xlsx');
      toast.success('Locations exported successfully');
    } catch (error) {
      console.error('Error exporting locations:', error);
      toast.error('Failed to export locations');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="bg-[var(--color-surface)] rounded-lg shadow-md p-6 font-[var(--font-family)] text-[var(--color-text)]">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--color-text)', fontFamily: 'var(--font-family)' } as any}>Locations</h1>
          <p style={{ color: 'var(--color-text)', fontFamily: 'var(--font-family)' } as any}>
            Manage your product storage locations
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => { 
              apiService.invalidateMasterDataCache(); 
              fetchLocations(); 
            }}
            title="Refresh"
            className="flex items-center justify-center p-2 rounded-lg hover:bg-gray-50 transition-colors bg-transparent"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <Button
            variant="primary"
            onClick={() => setShowAddModal(true)}
            icon={<Plus className="w-5 h-5" />}
          >
            Add Location
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-[var(--color-surface)] rounded-lg shadow-sm p-4" style={{ borderRadius: 'var(--border-radius)' } as any}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--color-text-secondary)] w-5 h-5" />
              <input
                type="text"
                placeholder="Search locations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-4 py-2 border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
            >
              <Filter className="w-4 h-4" />
              <span>Filters</span>
            </button>
          </div>
          <button
            onClick={exportLocations}
            className="flex items-center space-x-2 px-4 py-2 border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
          >
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
        </div>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 } as any}
            animate={{ opacity: 1, height: 'auto' } as any}
            exit={{ opacity: 0, height: 0 } as any}
            className="mt-4 pt-4 border-t border-[var(--color-border)]"
          >
            <div className="flex items-center space-x-4">
              <div>
                <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-surface)] text-[var(--color-text)]"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Locations Table */}
      <div className="bg-[var(--color-surface)] rounded-lg shadow-sm overflow-hidden" style={{ borderRadius: 'var(--border-radius)' } as any}>
        <DoubleScrollbar>
          <table className="min-w-full divide-y divide-[var(--color-border)]">
            <thead className="bg-[var(--color-surface-light)]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Address
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-[var(--color-background)] divide-y divide-[var(--color-border)]">
              {filteredLocations.map((location) => (
                <tr key={location._id} className="hover:bg-[var(--color-surface-light)]">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <MapPin className="w-5 h-5 text-[var(--color-primary)] mr-2" />
                      <div className="text-sm font-medium text-[var(--color-text)] font-['Times_New_Roman']">
                        {location.name}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">
                    {typeof location.address === 'string' ? location.address : (typeof location.address === 'object' && location.address?.street) ? location.address.street : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">
                    {location.description || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-['Times_New_Roman']">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full font-['Times_New_Roman'] ${location.status === 'active' ? 'bg-[var(--color-success-light)] text-[var(--color-success-dark)]' : 'bg-[var(--color-danger-light)] text-[var(--color-danger-dark)]'}`}>{location.status}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">{formatDateTime(location.createdAt, settings)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => openEditModal(location)}
                        className="text-[var(--color-primary)] hover:text-[var(--color-primary-dark)] font-['Times_New_Roman']"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteLocation(location._id)}
                        className="text-[var(--color-danger)] hover:text-[var(--color-danger-dark)] font-['Times_New_Roman']"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DoubleScrollbar>
        {/* Empty State */}
        {filteredLocations.length === 0 && (
          <div className="text-center py-12">
            <MapPin className="w-16 h-16 text-[var(--color-text-secondary)] mx-auto mb-4" />
            <h3 className="text-lg font-medium text-[var(--color-text)] mb-2 font-['Times_New_Roman']">
              No locations found
            </h3>
            <p className="text-[var(--color-text-secondary)] font-['Times_New_Roman']">
              {searchTerm || statusFilter ? 'Try adjusting your search or filters.' : 'Get started by creating your first location.'}
            </p>
          </div>
        )}
      </div>
      {/* Add Location Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add New Location"
      >
        <div className="space-y-4">
          <Input
            label="Name"
            value={newLocation.name}
            onChange={e => setNewLocation({ ...newLocation, name: e.target.value })}
            placeholder="Enter location name"
            required
          />
          <Input
            label="Address"
            value={newLocation.address}
            onChange={e => setNewLocation({ ...newLocation, address: e.target.value })}
            placeholder="Enter address"
          />
          <Input
            label="Description"
            value={newLocation.description}
            onChange={e => setNewLocation({ ...newLocation, description: e.target.value })}
            placeholder="Enter location description"
          />
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">
              Status
            </label>
            <select
              value={newLocation.status}
              onChange={e => setNewLocation({ ...newLocation, status: e.target.value as 'active' | 'inactive' })}
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={() => setShowAddModal(false)}
            className="px-4 py-2 text-[var(--color-text-secondary)] border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
          >
            Cancel
          </button>
          <button
            onClick={handleAddLocation}
            className="px-4 py-2 bg-[var(--color-primary)] text-[var(--color-on-primary)] rounded-lg hover:bg-[var(--color-primary-dark)] transition-colors font-['Times_New_Roman']"
          >
            Add Location
          </button>
        </div>
      </Modal>
      {/* Edit Location Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Location"
      >
        {editingLocation && (
          <div className="space-y-4">
            <Input
              label="Name"
              value={editingLocation.name}
              onChange={e => setEditingLocation({ ...editingLocation, name: e.target.value })}
              placeholder="Enter location name"
              required
            />
            <Input
              label="Address"
              value={typeof editingLocation.address === 'string' ? editingLocation.address : (typeof editingLocation.address === 'object' && editingLocation.address?.street) ? editingLocation.address.street : ''}
              onChange={e => setEditingLocation({ ...editingLocation, address: e.target.value })}
              placeholder="Enter address"
            />
            <Input
              label="Description"
              value={editingLocation.description || ''}
              onChange={e => setEditingLocation({ ...editingLocation, description: e.target.value })}
              placeholder="Enter location description"
            />
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">
                Status
              </label>
              <select
                value={editingLocation.status}
                onChange={e => setEditingLocation({ ...editingLocation, status: e.target.value as 'active' | 'inactive' })}
                className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        )}
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={() => setShowEditModal(false)}
            className="px-4 py-2 text-[var(--color-text-secondary)] border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
          >
            Cancel
          </button>
          <button
            onClick={handleEditLocation}
            className="px-4 py-2 bg-[var(--color-primary)] text-[var(--color-on-primary)] rounded-lg hover:bg-[var(--color-primary-dark)] transition-colors font-['Times_New_Roman']"
          >
            Update Location
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default LocationsPage; 